<script setup>
/**
 * FormFieldset
 * Manages the rendering of a group of form fields. It handles dynamic field filtering
 * based on legends (e.g., "SEO", "Scheduling") and ensures the UI can scroll correctly
 * to the last fieldset by injecting dynamic margin.
 */
import {computed, onMounted, nextTick, ref} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useFormStore} from '@/stores/form'
import FormGroup from './FormGroup'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    isFirst: Boolean,
    isLast: Boolean,
    legend: {
        type: String,
        default: ''
    },
    showLegends: Boolean,
    hideHelpText: Boolean
})

// Template Ref
const fieldsetRef = ref(null)

// Pinia Stores
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const tableName = computed(() => formStore.value?.tableName)

const fieldsetId = computed(() => props.legend.replace(/[^A-Z0-9]+/ig, '_'))

const fields = computed(() => formStore.value?.fields || [])

const formSmall = computed(() => formStore.value?.formSmall)

const fieldsetsFields = computed(() => {
    let filteredFields = []
    const fieldsetMetadata = fieldsetsStore.items

    if (fieldsetMetadata.length) {
        if (props.legend === 'Scheduling') {
            filteredFields = fields.value.filter(o => ['validFrom', 'validTo'].indexOf(o.columnName) > -1)
        } else if (props.legend === 'SEO') {
            filteredFields = fields.value.filter(o => ['metaTitle', 'metaDescription', 'noIndexNoFollow'].indexOf(o.columnName) > -1)
        } else {
            const componentId = componentsStore.componentIds[tableName.value]
            const meta = fieldsetMetadata.find(o => o.componentId === componentId && o.legend === props.legend)

            if (meta) {
                filteredFields = fields.value
                    .filter(o => o.fieldsetId === meta.id)
                    .sort((a, b) => (a.displayOrder > b.displayOrder ? 1 : -1))
            }
        }
    }

    return filteredFields
})

// --- Methods ---

const resizeLastFieldset = () => {
    if (props.isLast) {
        nextTick(() => {
            const fieldset = fieldsetRef.value
            if (!fieldset) return

            const scrollableEl = fieldset.closest('.sidebar-toggle-section')
            if (scrollableEl) {
                // Calculation ensures that the anchor links can scroll the last fieldset to the top of the viewport
                const formTop = fieldset.closest('form').getBoundingClientRect().top
                const marginBottom = window.innerHeight - fieldset.offsetHeight - formTop

                if (marginBottom > 0) {
                    fieldset.style.marginBottom = marginBottom + 'px'
                }
            }
        })
    }
}

// --- Lifecycle ---

onMounted(() => {
    resizeLastFieldset()
})
</script>

<template>
    <fieldset :id="fieldsetId" ref="fieldsetRef" :name="legend">
        <legend v-if="!isFirst && showLegends" class="legend mt-5">
            {{ legend }}
        </legend>

        <FormGroup
            v-for="field in fieldsetsFields"
            :key="field.id"
            :field="field"
            :form-id="formId"
            :hide-help-text="hideHelpText"
            class="pb-3"
            :class="{'mb-3': formSmall, 'mb-4': !formSmall}"
        />
    </fieldset>
</template>

<style scoped>
.legend {
    margin-bottom: 0;
    padding: 0 0 15px 0;
    color: #999;
    font-size: 36px;
    font-weight: 300;
    border-bottom: none;
}
</style>